import React from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { config } from "../config";
import { useEventsApi } from "../hooks/useEventsApi";

type HistoryRow = {
  global_event_id: number;
  lineage_id: number;
  event_code: number;
  event_type: string;
  event_contract_name: string;
  version: number;
  approved_at?: string | null;
  is_superseded: boolean;
  superseded_by?: number | null;
};

const EventHistoryExplorer: React.FC = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { callApi } = useEventsApi();
  const [eventCode, setEventCode] = React.useState(searchParams.get("event_code") || "");
  const [rows, setRows] = React.useState<HistoryRow[]>([]);
  const [lineageIds, setLineageIds] = React.useState<number[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  const runSearch = async (overrideCode?: string) => {
    setError(null);
    const url = new URL(`${config.apiUrl}/api/events/history-chain/`);
    const code = (overrideCode ?? eventCode).trim();
    if (!code) {
      setError("Enter an event code.");
      return;
    }
    url.searchParams.set("event_code", code);
    try {
      const data = await callApi(url.toString(), "GET");
      setRows(data.results || []);
      setLineageIds(Array.isArray(data.lineage_ids) ? data.lineage_ids : []);
      const next = new URLSearchParams(searchParams);
      next.set("event_code", code);
      setSearchParams(next, { replace: true });
    } catch (e: any) {
      setRows([]);
      setLineageIds([]);
      setError(e?.responseBody || e?.message || "History search failed.");
    }
  };

  React.useEffect(() => {
    const code = (searchParams.get("event_code") || "").trim();
    if (!code) return;
    setEventCode(code);
    void runSearch(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h5" sx={{ mb: 1, color: theme.palette.text.primary }}>
        Event history explorer
      </Typography>
      <Typography
        variant="body2"
        sx={{ mb: 2, color: theme.palette.text.secondary }}
      >
        Analyze reassignment transition chains across versions.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "background.paper", color: "text.primary" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} flexWrap="wrap">
          <TextField
            size="small"
            label="Event code"
            value={eventCode}
            onChange={(e) => setEventCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void runSearch();
              }
            }}
          />
          <Button variant="contained" onClick={() => void runSearch()}>Search chain</Button>
        </Stack>
        {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
      </Paper>

      {lineageIds.length > 0 && (
        <Typography
          variant="subtitle2"
          sx={{ mb: 1, color: theme.palette.text.primary, fontWeight: 600 }}
        >
          Lineage IDs: {lineageIds.join(", ")}
        </Typography>
      )}

      <Paper variant="outlined" sx={{ bgcolor: "background.paper", color: "text.primary" }}>
        <Table size="small" sx={{ "& .MuiTableCell-root": { borderColor: "divider" } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Version</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Event code</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Contract</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Superseded</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Superseded by</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.global_event_id}>
                <TableCell>{r.global_event_id}</TableCell>
                <TableCell>v{r.version}</TableCell>
                <TableCell>{r.event_code}</TableCell>
                <TableCell>{r.event_contract_name}</TableCell>
                <TableCell>{r.is_superseded ? "yes" : "no"}</TableCell>
                <TableCell>{r.superseded_by ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default EventHistoryExplorer;
